/** @jsxImportSource @emotion/react */
import classNames from 'classnames';
import { IconX } from 'featherico';
import { noop } from 'lodash/fp';
import PropTypes from 'prop-types';
import * as React from 'react';
import { useEffect, useLayoutEffect, useState } from 'react';
import ReactModal from 'react-modal';
import useIdGen from '../../hooks/useIdGen';
import Button from '../Button';
import Link from '../Link';
import UpgradePlanAlert from '../UpgradePlan/UpgradePlanAlert';
import SIZES, { ModalSize } from './sizes';
import styles from './style.m.less';

const TRANSITION_DURATION = 150;

const STATUS = {
    info: styles.statusInfo,
    success: styles.statusSuccess,
    waiting: styles.statusWaiting,
    error: styles.statusError,
};

const asSizeValue = (v: number | string) => (typeof v === 'number' ? `${v}px` : v);

/**
 * Isolates the click (so it doesn't bubble to the outside).
 */

interface ModalContentProps {
    withPadding: boolean;
    className?: string;
}

function ModalContent<P extends ModalContentProps>({ withPadding, className = undefined, ...rest }: P) {
    return (
        <div
            className={classNames(
                {
                    [styles.contentInnerPadding]: withPadding,
                },
                className
            )}
            role="presentation"
            {...rest}
        />
    );
}

const parseMinHeight = (minHeight?: number | string) => {
    if (!minHeight) {
        return 'auto';
    }
    return typeof minHeight === 'number' ? `${minHeight}px` : minHeight;
};

interface ModalTitleProps {
    id?: string;
    children?: React.ReactNode;
    titleAlignedCenter?: boolean;
    noCloseIcon?: boolean;
    showPremiumAlert?: boolean;
}
export function ModalTitle({
    children = undefined,
    id = undefined,
    titleAlignedCenter = undefined,
    noCloseIcon = undefined,
    showPremiumAlert = false,
}: ModalTitleProps) {
    const cN = classNames(styles.title, {
        [styles.centered]: titleAlignedCenter,
        [styles.marginWithCloseIcon]: !noCloseIcon,
        [styles.noMarginBottom]: showPremiumAlert,
    });
    const cNWrapper = classNames(styles.wrapper, {
        [styles.marginWithPremiumAlert]: !noCloseIcon,
        [styles.flexRow]: showPremiumAlert,
    });

    return (
        <div className={cNWrapper}>
            <h1 id={id} className={cN}>
                {children}
            </h1>
            {showPremiumAlert && (
                <UpgradePlanAlert
                    showTooltip
                    feature="FEATURE_cycleAdmins"
                    tootlipContent={
                        <>
                            This is a premium feature not available on your current plan. Reach out to{' '}
                            <Link href="mailto:support@small-improvements.com">support@small-improvements.com</Link> for
                            details.
                        </>
                    }
                ></UpgradePlanAlert>
            )}
        </div>
    );
}

Modal.propTypes = {
    isOpen: PropTypes.bool,
    onClose: PropTypes.func,
    width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    noCloseIcon: PropTypes.bool,
    backgroundImage: PropTypes.string,
    backgroundRepeat: PropTypes.string,
    backgroundSize: PropTypes.string,
    minHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    size: PropTypes.oneOf(Object.keys(SIZES)),
    status: PropTypes.oneOf(Object.keys(STATUS)),
    topAligned: PropTypes.bool,
    title: PropTypes.node,
    titleAlignedCenter: PropTypes.bool,
    fullScreen: PropTypes.bool,
    fullHeight: PropTypes.bool,
    requireExplicitClose: PropTypes.bool,
    className: PropTypes.string,
    withPadding: PropTypes.bool,
};

export interface ModalProps extends ReactModal.Props {
    onClose?: () => void;
    width?: number | string;
    noCloseIcon?: boolean;
    backgroundImage?: string;
    backgroundRepeat?: string;
    backgroundSize?: string;
    minHeight?: string | number;
    size?: ModalSize;
    status?: keyof typeof STATUS;
    topAligned?: boolean;
    title?: React.ReactNode;
    titleAlignedCenter?: boolean;
    fullScreen?: boolean;
    fullHeight?: boolean;
    requireExplicitClose?: boolean;
    className?: string;
    withPadding?: boolean;
    showPremiumAlert?: boolean;
    children: React.ReactNode;
}

export default function Modal({
    isOpen,
    onClose,
    width,
    noCloseIcon,
    backgroundImage,
    backgroundRepeat,
    backgroundSize,
    minHeight,
    size = 'XL_WIDTH',
    status,
    topAligned = false,
    title,
    titleAlignedCenter = false,
    fullScreen = false,
    fullHeight = false,
    requireExplicitClose,
    className,
    withPadding = true,
    showPremiumAlert,
    children,
    ...otherProps
}: ModalProps) {
    useLayoutEffect(() => {
        ReactModal.setAppElement(document.getElementsByTagName('main')[0] || document.body);
    }, []);

    const id = useIdGen();
    const labelId = id('title');

    const [wasOpened, setWasOpened] = useState(isOpen);
    useEffect(() => {
        if (isOpen) {
            setWasOpened(true);
        }
    }, [isOpen]);

    if (!wasOpened) {
        return null;
    }

    const handleClose = (event: React.MouseEvent) => {
        event.stopPropagation();
        if (onClose) {
            onClose();
        }
    };

    const style = {
        overlay: {
            transitionDuration: TRANSITION_DURATION + 'ms',
        },
        content: {
            backgroundImage: backgroundImage ? `url(${backgroundImage})` : '',
            backgroundRepeat: backgroundRepeat || 'no-repeat',
            backgroundSize: backgroundSize,
            width: fullScreen ? '100%' : width || asSizeValue(SIZES[size]),
            minHeight: fullScreen ? '100%' : parseMinHeight(minHeight),
        },
    };

    const content = (
        <ModalContent withPadding={withPadding}>
            {title && (
                <ModalTitle
                    id={labelId}
                    titleAlignedCenter={titleAlignedCenter}
                    noCloseIcon={noCloseIcon}
                    showPremiumAlert={showPremiumAlert}
                >
                    {title}
                </ModalTitle>
            )}
            {!noCloseIcon && <ModalCloseButton handleClose={handleClose} />}
            <div className={classNames(styles.contentInnerInner, className)}>{children}</div>
        </ModalContent>
    );
    const modalProps = {
        ...otherProps,
        onRequestClose: requireExplicitClose ? noop : handleClose,
        closeTimeoutMS: TRANSITION_DURATION,
        style,
        aria: {
            labelledby: labelId,
            modal: true,
        },
        role: 'dialog',
        children: content,
        className: classNames(styles.content, status ? STATUS[status] : undefined),
        overlayClassName: classNames([styles.overlay], {
            [styles.overlayTopAligned]: topAligned,
            [styles.overlayFullHeight]: fullHeight,
            [styles.overlayBackdrop]: !fullScreen,
        }),
        isOpen,
    };

    return <ReactModal {...modalProps} />;
}

export function ModalCloseButton({ handleClose }: { handleClose: (event: React.MouseEvent) => void }) {
    return (
        <Button kind="PLAIN" className={styles.closeButton} onClick={handleClose} aria-label="Close">
            <IconX />
        </Button>
    );
}
